import { Divider, Tabs } from "antd";
import "./Features.css";
import PrimaryButton from "../../../../components/Buttons/PrimaryButton/PrimaryButton";
import { Form } from "antd";
import CustomCheckbox from "../../../../components/CustomCheckbox/CustomCheckbox";
import React, { useState, useEffect } from "react";
import centralApi from "../../../../services/centralApi";
import Spinner from "../../../../components/Spinner/Spinner";
import NoData from "../../../../components/NoData/NoData";
import { t } from "i18next";
import { API_ENDPOINTS } from "utils/constants";
import { useDispatch, useSelector } from "react-redux";
import { setFeatureSettings } from "redux/features/app/featuresSettingSlice";
import useMessage from "hooks/useMessage";
import { BOOLEAN_OPTIONS } from "utils/constants/selectorOptions";
import { getToken } from "redux/features/app/authTokenSlice";
import { InfoCircleOutlined } from "@ant-design/icons";

const Features: React.FC = () => {
  const dispatch: any = useDispatch();
  const featureData = useSelector(
    (state: any) => state.featuresSetting?.data[0]?.features || {}
  );
  const { showError, showSuccess } = useMessage();
  const [showNoDataMessage, setShowNoDataMessage] = useState(false);
  const [loading, setLoading] = useState(false);

  const loginUser = useSelector((state: any) => state.loginUser);
  const activeApp = useSelector((state: any) => state.activeApp);
  const authToken = useSelector((state: any) => state.authToken.token);

  const appId = activeApp?.appId;
  const api_key = loginUser?.data.api_key;

  // Grouped feature labels with their keys
  const featureGroups = {
    dashboard: ["cohortChart"],
    customer360: [
      "fetchEventsFromMongo",
      "maskedDids",
      "genericProfile",
      "campaignAuditTrail"
    ],
    audience: [
      "audienceV2",
      "audience_exports",
    ],
    traits: ["traitPreview"],
    acquisition: ["acquisitionByType", "customTopic"],
    funnel: ["funnelV2", "funnelPeriod", "funnelLaunchbtn"],
    "campaign-analytics": [
      "campaignSummaryCsvDownload",
      "campDeletion",
      "makerChecker",
      "downloadCsv",
      "sendEmailOnCampActivation"
    ],
    create: [
      "advanceSetting",
      "ab_testing_variant",
      "language_variant",
      "actionbuttonSettings",
      "specialattributes",
      "email_editor",
      "embedCustomField",
      "liveActivity",
      "landingPageView",
      "category",
      "escapeChars",
      "actionButtonCopyShare"

    ],
    "launch-campaign": [
      "dismissPushfromTray",
      "liveEventSett",
      "schedulerSett",
      "crossButtonStyle",
      "launchCampFreq",
      "optimisation",
      "advancedFreq",
      "dayPart",
      "conversionSettings",
      "isConsentHonoured",
      "isSettingsMode",
      "campaignShowenInSession",
      "intervalBetweenMessage",
      "staggeredSend",
      "budget",
      "category",
      "appInboxAndDeliveryType",
      "rateLimit",
      "dryRun"
    ],
    "campaign-details": [
      "appInboxReports",
      "exportCampaignDataCsv",
      "campaignDetailsCsv",
      "campaignReportDatewiseGraph",
      "showPlatformWiseData",
      "staggeredSend",
      "budget"
    ],

    automationProduct: [
      "campDeletion",
      "cmpInsertInToPG",
      "makerChecker",
      "downloadCsv",
      "crossButtonStyle",
      "maskedDids", 
      "escapeChars",
      "multiStep",
      "dedup",
      "useHttp1Protocol",
      "fetchEventsFromMongo",
      "genericProfile",
      "campaignAuditTrail",
      "topicOnly",
      "segmentedPush",
      "getAppEnabledForPush",
      "getAppEnabledForGeo",
      "campaignThreshold",
      "isAutomaticUploadOfCertificates"

    ],
    analytics: [
      "funnelV2",
      "audienceV2",
      "funnelCountTypeToggle",
      "funnelPeriod",
      "audience_exports",
      "traitPreview",
      "journeynavsettings",
      "removeWeb",
      "acquisitionByType",
    ],
    customerDataPlatform: [
      "demographics",
      "usage",
      "reports",
      "transactionalCamp",
      "allyvateAi",
      "loyalty",
      "performanceReports",
      "attributionReport",
      "customer360Mapping",
      "personlizationMapping",
      "setupAppsSettings",
      "churn",
      "documentation",
      "displayPreference",
      "manageModules",
      "analyze",
      "shortener",
      "builder",
      "links",
      "audienceSync",
      "functions",
      "journey",
      "funnel",
      "appAcquistion",
      "inSights",
      "traits",
      "customer360",
      "dataManagement",
      "referenceData",
    ],
    legacy: [
      "userprofileImg",
      "outsideTheApp",
      "journeynavsettings",
      "competingApps",
      "whowhatwherewhen",
      "deepLinkUrl",
      "cSegPhoneEmail",
      "deviceAttributes",
      "appUsageAttributes",
      "allowList",
      "campExceptPush",
      "advPush",
      "delayCamp",
      "expImg",
      "removeWeb",
      "hidegranularMonetaryCACLTV",
      "downloadSDK",
    ],
    pipeline: [
      "topicOnly",
      "segmentedPush",
      "getAppEnabledForPush",
      "getAppEnabledForGeo",
      "multiStep",
      "dedup",
      "useHttp1Protocol",
      "cmpInsertInToPG",
      "parallelJobs"
    ],
    api: ["campaignThreshold"],
  };

  // Labels mapping
  const labels: { [key: string]: string } = {
    demographics: t("demographicsLabel"), //hide demographics page
    language_variant: t("languageVariantLabel"), //hide language variants
    ab_testing_variant: t("abTestingVariantLabel"), //hide variansts
    usage: t("usageLabel"), // hide usage page in  side nav under analytics
    reports: t("reportsLabel"), // hides reports drowpdown in side nav
    appInboxReports: t("appInboxReportsLabel"), //hides App Inbox reports on the campaign details page.
    maskedDids: t("maskedDidsLabel"),
    churn: t("churnLabel"), //hides churn page
    expImg: t("expImgLabel"), //hide expaned image in v1
    advPush: t("advPushLabel"), // hide advance push
    campExceptPush: t("campExceptPushLabel"), //hides all campaign except push in v1
    transactionalCamp: t("transactionalCampLabel"), //hides tansactional campaign page
    performanceReports: t("performanceReportsLabel"), // hides campaign reports from side nav
    userprofileImg: t("userprofileImgLabel"),
    optimisation: t("optimisationLabel"), //hides optimisaton from launch camp page
    appInboxAndDeliveryType: t("appInboxAndDeliveryTypeLabel"),
    funnelPeriod: t("funnelPeriodLabel"),
    documentation: t("documentationLabel"), //hides documnetation page from side nav
    outsideTheApp: t("outsideTheAppLabel"), // hides outside the app
    campaignDetailsCsv: t("campaignDetailsCsvLabel"), //hides campaign details button on campaign details page
    competingApps: t("competingAppsLabel"),
    hidegranularMonetaryCACLTV: t("hidegranularMonetaryCACLTVLabel"),
    embedCustomField: t("embedCustomFieldLabel"), //hides to add  personalisation feature
    whowhatwherewhen: t("whowhatwherewhenLabel"), //hides who what where when in v1
    deepLinkUrl: t("deepLinkUrlLabel"), // hides deep linl url in push campaign in v1
    category: t("categoryLabel"), //hides category in launch campaign page
    email_editor: t("emailEditorLabel"), //hides email editor in email campaign
    landingPageView: t("landingPageViewLabel"), //hides landing page view in createLabel
    funnelLaunchbtn: t("funnelLaunchbtnLabel"), //hides launch campaign button in funnel details
    cmpInsertInToPG: t("cmpInsertInToPGLabel"), //automates campaign insertion in pg
    advancedFreq: t("advancedFreqLabel"), // hides advance frequency in launch campaign page
    launchCampFreq: t("launchCampFreqLabel"), //hides frequency capping in launch campaign.
    audience_exports: t("audienceExportsLabel"), //hides audience export
    actionbuttonSettings: t("actionButtonSettingsLabel"), //hides action buttons
    specialattributes: t("specialAttributesLabel"), //hides speacial attributes in push campaign
    conversionSettings: t("conversionSettingsLabel"), //hides conversion setting in launch cmapaign page
    journeynavsettings: t("journeyNavSettingsLabel"), //hides journey nav
    setupAppsSettings: t("setupAppsSettingsLabel"), //hides setup apps
    delayLauch: t("delayLaunchLabel"), //hides delay launch campaign option in launch campaign page
    removeWeb: t("removeWebLabel"), // hides web option from filters
    liveEventSett: t("liveEventSettLabel"), // hides Live Event option in launch campaign
    schedulerSett: t("schedulerSettLabel"), // hides scheduler settings in launch campaign
    cSegPhoneEmail: t("phone/emailLabel"), //hides in v1 audience we enable/disable email phone  from ist dropdown
    allyvateAi: t("allyvateAiLabel"), // hides allyvate ai from deom side nav
    loyalty: t("loyaltyLabel"), //hides loyalty ption from side nav
    deviceAttributes: t("deviceAttributesLabel"), //in v1 audience we used to enable or disable attributes in audience page
    appUsageAttributes: t("appUsageAttributesLabel"), //hides  some events/attributes in panel v1 audience create page
    traits: t("traitsLabel"), //hides traits from side nav
    functions: t("functionsLabel"), // hides functions from side nav
    audienceSync: t("audienceSyncLabel"), //hides DemoSync from  side nav
    allowList: t("allowListLabel"), // hide allow list in app settings
    downloadSDK: t("downloadSdkLabel"), //hides download sdk option button from app setting page
    manageModules: t("manageModulesLabel"), // hides manage modules from side nav
    displayPreference: t("displayPreferenceLabel"), // hides dispay preference from side nav
    delayCamp: t("delayCampLabel"), //hides delaycamp from launch campaign page
    acquisitionByType: t("acquisitionTypeLabel"), // hides acquisition by type table in acquisition page
    customTopic: t("customTopicLabel"),
    isConsentHonoured: t("consentChecklLabel"), //hides consent check from launch campaign pag
    inSights: t("inSightsLabel"), // hides insights from side nav
    isSettingsMode: t("modeLabel"), // hides mode from launch campaign page
    dayPart: t("dayPartLabel"), // hides day part from launch campiagn page
    liveActivity: t("liveActivityLabel"), // hides liveactivity from push campaign template
    campaignShowenInSession: t("campaignShowenInSessionLabel"), //hides  number of times a campaign is shown in a session (under frequency capping in launch campaign page)
    intervalBetweenMessage: t("intervalBetweenMessageLabel"), // hides interval between messages in launch campaign page
    links: t("linksLabel"), // hies link from side nav
    shortener: t("shortenerLabel"), // hides shortner from side nav
    builder: t("builderLabel"), //hides builder from side nav
    customer360: t("customer360Label"), // hides Customer360 page from sidebar navigation under Users
    attributionReport: t("attributionReportLabel"), // hides attribution report from side nav
    customer360Mapping: t("customer360MappingLabel"), //hides customer 360 under data management in side nav
    personlizationMapping: t("personlizationMappingLabel"), // hides persionalisation mapping under datamanagement in side nav
    funnelV2: t("funnelV2Label"), //enables funnelv2 features
    audienceV2: t("audienceV2Label"), // enables audience v2 feature
    funnelCountTypeToggle: t("funnelCountTypeToggleLabel"), // to toggle between count type and period type in funnel v2
    cohortChart: t("cohortChartLabel"), // hdies cohort chart on dashboard
    journey: t("journeyLabel"), // hides journey in side nav under analytics
    funnel: t("funnelLabel"), // hides funnel in  side nav under analytics
    appAcquistion: t("appAcquistionLabel"), // hides app acquisition from side nav under analytics
    campDeletion: t("campDeletionLabel"), //Allow campaign to activate only once
    multiStep: t("multiStepCampLabel"), // automation for multistep campaign
    traitPreview: t("traitPreviewLabel"), // hides trait preview
    makerChecker: t("makerCheckerLabel"), // does not allow maker to activate the campaign only checker can do maker and cehcker cannot be same
    dedup: t("dedupLabel"), //automation feature if two device id have same number it restricts to send it twice
    analyze: t("analyzeLabel"), //Enable/Disable crash analytics from sidebar navigation
    escapeChars: t("escapeCharsLabel"), // does not allow escape charaters in push campaign template
    showPlatformWiseData: t("showPlatformWiseDataLabel"), // allow to show platform wise data in transactional campaign in campaign details page
    crossButtonStyle: t("crossButtonStyleLabel"), // allows cross style for in app screen if enabled then 1 if disabled then 0 will be set v
    exportCampaignDataCsv: t("exportCampaignDataCsvLabel"), //Enable or disable to hide the export campaign data csv feature in campaign details page
    campaignSummaryCsvDownload: t("allCampaignsSummaryCsvDownloadLabel"), //Enable or disable download option button in all campaign summary page
    dataManagement: t("dataManagementLabel"), //hide data management from side nav
    referenceData: t("referenceDataLabel"), //hide reference data from side nav
    useHttp1Protocol: t("useHttp1ProtocolLabel"), //"when http1 protocal method enabled  mean uncheck then send  method in firebase if disabled then send each  method
    downloadCsv: t("downloadCampaignSummaryLabel"), // allow user to download csv time wise if disable then day wise
    advanceSetting: t("advanceSettingPushLabel"), // hide advance setting push campaign template
    fetchEventsFromMongo: t("fetchEventsFromMongoLabel"), //show events in customer360 from mongo instead of citus
    topicOnly: t("topicOnlyLabel"), // to send push using topic only
    segmentedPush: t("segmentedPushLabel"), //to check whether the user is in segmenet or not
    dismissPushfromTray:t("dismissPushfromTrayLabel"), //hide dismiss push from tray in launch campaign page
    campaignReportDatewiseGraph:t("campaignDatewiseGraphLabel"), //hide graph in cmapaign details page
    getAppEnabledForPush:t("getAppEnabledForPushLabel"),// Enables getApp for push
    getAppEnabledForGeo:t("getAppEnabledForGeoLabel"), //Enable getApp for geo
    genericProfile:t("genericProfileLabel"),
    campaignThreshold:t("campaignThresholdLabel"), //Enable or disable to allow campaigns insert in citus when campaign is set active  
    staggeredSend:t("staggeredSendLabel"), //Enable or disable to show staggered send in launch campaign page  
    budget:t("budgetLabel"), //Enable or disable to show budget setting in launch campaign page  
    rateLimit:t("rateLimitLabel"),
    isAutomaticUploadOfCertificates:t("isAutomaticUploadOfCertificatesLabel"), //enable disbale upload of certificate in ts core automatically
    campaignAuditTrail:t("campaignAuditTrailLabel"), //show customer audit trail in customer360 page enable or disable
    actionButtonCopyShare:t("actionButtonCopyShareLabel"), //show copy share button in actionbuttons
    sendEmailOnCampActivation:t("sendEmailOnCampActivationLabel"), //show copy share button in actionbuttons
    parallelJobs:t("parallelJobsLabel"), //enable or disable parallel jobs in backend pipeline
    dryRun:t("dryRunLabel") //dry run in launch cmapaign page

  };
  /**
   * Handles toggle change for each feature checkbox
   */
  const handleChange = (featureKey: string, newValue: boolean) => {
    dispatch(setFeatureSettings({ [featureKey]: newValue }));
  };

  /**
   * Handles form submission - formats data and calls API
   */
  const handleSubmit = async () => {
    const labelToKeyMapping: { [label: string]: string } = {
      demographicsState: "demographics",
      usageState: "usage",
      reportsState: "reports",
      appInboxReports: "appInboxReports",
      maskedDids: "maskedDids",
      churnState: "churn",
      expImgState: "expImg",
      advPushState: "advPush",
      campExceptPushState: "campExceptPush",
      transState: "transactionalCamp",
      performanceReports: "performanceReports",
      userprofileImgState: "userprofileImg",
      hidegranularMonetaryCACLTV: "hidegranularMonetaryCACLTV",
      optimisationState: "optimisation",
      appInboxDeliveryState: "appInboxAndDeliveryType",
      funnelPeriod: "funnelPeriod",
      documentationState: "documentation",
      outsideTheAppState: "outsideTheApp",
      campaignDetailsCsv: "campaignDetailsCsv",
      competingApps: "competingApps",
      embedCustomField: "embedCustomField",
      whowhatwherewhen: "whowhatwherewhen",
      audience_exports: "audience_exports",
      deepLinkUrl: "deepLinkUrl",
      category: "category",
      email_editor: "email_editor",
      landingPageView: "landingPageView",
      funnelLaunchbtn: "funnelLaunchbtn",
      language_variant: "language_variant",
      ab_testing_variant: "ab_testing_variant",
      cmpInsertInToPG: "cmpInsertInToPG",
      advancedFreq: "advancedFreq",
      launchCampFreq: "launchCampFreq",
      actionbuttonSettings: "actionbuttonSettings",
      specialattributes: "specialattributes",
      conversionSettings: "conversionSettings",
      journeynavsettings: "journeynavsettings",
      setupAppsSettings: "setupAppsSettings",
      delayLauch: "delayLauch",
      removeWeb: "removeWeb",
      liveEventSett: "liveEventSett",
      schedulerSett: "schedulerSett",
      cSegPhoneEmail: "cSegPhoneEmail",
      allyvateAi: "allyvateAi",
      loyalty: "loyalty",
      deviceAttributes: "deviceAttributes",
      appUsageAttributes: "appUsageAttributes",
      functions:"functions",
      traits:"traits",
      audienceSync:"audienceSync",
      allowList : "allowList",
      downloadSDK:"downloadSDK",
      manageModules:"manageModules",
      displayPreference:"displayPreference",
      delayCamp:"delayCamp",
      acquisitionByType:"acquisitionByType",
      isConsentHonoured:"isConsentHonoured",
      inSights:"inSights",
      isSettingsMode:"isSettingsMode",
      dayPart:"dayPart",
      liveActivity:"liveActivity",
      campaignShowenInSession:"campaignShowenInSession",
      intervalBetweenMessage:"intervalBetweenMessage",
      links:"links",
      shortener:"shortener",
      builder:"builder",
      customer360:"customer360",
      attributionReport:"attributionReport",
      customer360Mapping:"customer360Mapping",
      personlizationMapping:"personlizationMapping",
      funnelV2:"funnelV2",
      funnelCountTypeToggle:"funnelCountTypeToggle",
      audienceV2:"audienceV2",
      cohortChart:"cohortChart",
      journey:"journey",
      funnel:"funnel",
      appAcquistion:"appAcquistion",
      campDeletion:"campDeletion",
      multiStep:"multiStep",
      traitPreview:"traitPreview",
      makerChecker:"makerChecker",
      dedup:"dedup",
      downloadCsv:"downloadCsv",
      advanceSetting:"advanceSetting",
      analyze:"analyze",
      escapeChars: "escapeChars",
      showPlatformWiseData: "showPlatformWiseData",
      crossButtonStyle: "crossButtonStyle",
      exportCampaignDataCsv: "exportCampaignDataCsv",
      campaignSummaryCsvDownload: "campaignSummaryCsvDownload",
      sendEmailOnCampActivation:"sendEmailOnCampActivation",
      useHttp1Protocol: "useHttp1Protocol",
      dataManagement: "dataManagement",
      referenceData: "referenceData",
      fetchEventsFromMongo:"fetchEventsFromMongo",
      topicOnly:"topicOnly",
      segmentedPush:"segmentedPush",
      dismissPushfromTray:"dismissPushfromTray",
      campaignReportDatewiseGraph:"campaignReportDatewiseGraph",
      getAppEnabledForPush:"getAppEnabledForPush",
      getAppEnabledForGeo:"getAppEnabledForGeo",
      genericProfile:"genericProfile",
      campaignAuditTrail:"campaignAuditTrail",
      campaignThreshold:"campaignThreshold",
      staggeredSend:"staggeredSend",
      budget:"budget",
      rateLimit:"rateLimit",
      isAutomaticUploadOfCertificates:"isAutomaticUploadOfCertificates",
      customTopic:"customTopic",
      actionButtonCopyShare:"actionButtonCopyShare",
      parallelJobs:"parallelJobs",
      dryRun:"dryRun"


    };

    const reversedFeatureData: Record<string, boolean> = {};

    for (const label in labelToKeyMapping) {
      if (Object.prototype.hasOwnProperty.call(labelToKeyMapping, label)) {
        const key = labelToKeyMapping[label];
        reversedFeatureData[label] = featureData[key] ?? BOOLEAN_OPTIONS.TRUE;

      }
    }

    const payload = {
      args: JSON.stringify({
        ...reversedFeatureData,
        app_id: appId,
      }),
      app_id: appId,
      authToken,
      api_key: api_key,
    };

    await saveFeatureSetting(payload);
  };
  //handle save settings
  async function saveFeatureSetting(formData: any) {
    const url: string = API_ENDPOINTS.SAVE_FEATURES_SETTINGS_URL;

    try {
      setLoading(true);
      await centralApi("POST", url, formData, null);
      showSuccess(t("sucessfullySavedLabel"));
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showError(t("somethingWrongLabel"));
    }
    dispatch(getToken());
  }

  useEffect(() => {
    // Check if featureData is empty or null
    if (Object.keys(featureData).length === 0) {
      setShowNoDataMessage(true);
    } else {
      setShowNoDataMessage(false);
    }
  }, [featureData]);
  // description for each label
  const descriptions: { [key: string]: string } = {
    ab_testing_variant: t("abTestingVariantDescriptionLabel"),
    language_variant: t("languageVariantDescriptionLabel"),
    appInboxReports: t("appInboxReportsDescriptionLabel"),
    demographics: t("demographicsStateDescriptionLabel"),
    usage: t("usageStateDescriptionLabel"),
    reports: t("reportsStateDescriptionLabel"),
    maskedDids: t("maskedDidsDescriptionLabel"),
    churn: t("churnStateDescriptionLabel"),
    expImg: t("expImgDescriptionLabel"),
    advPush: t("advPushDescriptionLabel"),
    campExceptPush: t("campExceptPushDescriptionLabel"),
    transactionalCamp: t("transactionalCampDescriptionLabel"),
    performanceReports: t("performanceReportsDescriptionLabel"),
    userprofileImg: t("userprofileImgDescriptionLabel"),
    hidegranularMonetaryCACLTV: t("hideGranularMonetaryCACLTVDescriptionLabel"),
    optimisation: t("optimisationDescriptionLabel"),
    appInboxAndDeliveryType: t("appInboxAndDeliveryTypeDescriptionLabel"),
    funnelPeriod: t("funnelPeriodDescriptionLabel"),
    documentation: t("documentationDescriptionLabel"),
    outsideTheApp: t("outsideTheAppDescriptionLabel"),
    campaignDetailsCsv: t("campaignDetailsCsvDescriptionLabel"),
    competingApps: t("competingAppsDescriptionLabel"),
    embedCustomField: t("embedCustomFieldDescriptionLabel"),
    whowhatwherewhen: t("whowhatwherewhenDescriptionLabel"),
    audience_exports: t("audienceExportsDescriptionLabel"),
    deepLinkUrl: t("deepLinkUrlDescriptionLabel"),
    category: t("categoryDescriptionLabel"),
    email_editor: t("emailEditorDescriptionLabel"),
    landingPageView: t("landingPageViewDescriptionLabel"),
    funnelLaunchbtn: t("funnelLaunchbtnDescriptionLabel"),
    cmpInsertInToPG: t("cmpInsertInToPGDescriptionLabel"),
    advancedFreq: t("advancedFrequencyDescriptionLabel"),
    launchCampFreq: t("launchCampaignFrequencyDescriptionLabel"),
    actionbuttonSettings: t("actionButtonSettingsDescriptionLabel"),
    specialattributes: t("specialAttributesDescriptionLabel"),
    conversionSettings: t("conversionSettingsDescriptionLabel"),
    journeynavsettings: t("journeyNavSettingsDescriptionLabel"),
    setupAppsSettings: t("setupAppsSettingsDescriptionLabel"),
    delayLauch: t("delayLaunchDescriptionLabel"),
    removeWeb: t("removeWebDescriptionLabel"),
    liveEventSett: t("liveEventSettingsDescriptionLabel"),
    schedulerSett: t("schedulerSettingsDescriptionLabel"),
    cSegPhoneEmail: t("cSegPhoneEmailDescriptionLabel"),
    allyvateAi: t("allyvateAiDescriptionLabel"),
    loyalty: t("loyaltyDescriptionLabel"),
    deviceAttributes: t("deviceAttributesDescriptionLabel"),
    appUsageAttributes: t("appUsageAttributesDescriptionLabel"),
    functions: t("functionsDescriptionLabel"),
    traits: t("traitsDescriptionLabel"),
    audienceSync: t("audienceSyncDescriptionLabel"),
    allowList: t("allowListDescriptionLabel"),
    downloadSDK: t("downloadSdkDescriptionLabel"),
    manageModules: t("manageModulesDescriptionLabel"),
    displayPreference: t("displayPreferenceDescriptionLabel"),
    delayCamp: t("delayCampDescriptionLabel"),
    acquisitionByType: t("acquisitionByTypeDescriptionLabel"),
    customTopic: t("customTopicDescriptionLabel"),
    isConsentHonoured: t("isConsentHonouredDescriptionLabel"),
    inSights: t("inSightsDescriptionLabel"),
    advanceSetting: t("advanceSettingDescriptionLabel"),
    journey: t("journeyNavDescriptionLabel"),
    funnel: t("funnelNavDescriptionLabel"),
    multiStep: t("multiStepDescriptionLabel"),
    escapeChars: t("escapeCharsDecriptionLabel"),
    analyze: t("analyzeDescriptionLabel"),
    funnelV2: t("funnelV2DescriptionLabel"),
    cohortChart: t("cohortChartDescriptionLabel"),
    appAcquistion: t("appAcquistionDescriptionLabel"),
    campDeletion: t("campDeletionDescriptionLabel"),
    traitPreview: t("traitPreviewDescriptionLabel"),
    makerChecker: t("makerCheckerDescriptionLabel"),
    dedup: t("dedupDescriptionLabel"),
    downloadCsv: t("downloadCsvDescriptionLabel"),
    isSettingsMode: t("isSettingsModeDescriptionLabel"),
    dayPart: t("dayPartDescriptionLabel"),
    liveActivity: t("liveActivityDescriptionLabel"),
    campaignShowenInSession: t("campaignShowenInSessionDescriptionLabel"),
    intervalBetweenMessage: t("intervalBetweenMessageDescriptionLabel"),
    links: t("linksDescriptionLabel"),
    shortener: t("shortenerDescriptionLabel"),
    builder: t("builderDescriptionLabel"),
    customer360: t("customer360DescriptionLabel"),
    attributionReport: t("attributionReportDescriptionLabel"),
    customer360Mapping: t("customer360MappingDescriptionLabel"),
    personlizationMapping: t("personlizationMappingDescriptionLabel"),
    audienceV2: t("audienceV2descriptionLabel"),
    funnelCountTypeToggle: t("funnelCountTypeToggleDescriptionLabel"),
    showPlatformWiseData: t("showPlatformWiseDataDescription"),
    crossButtonStyle: t("crossButtonStyleDescription"),
    exportCampaignDataCsv: t("exportCampaignDataCsvDescription"),
    campaignSummaryCsvDownload: t("allCampaignsSummaryCsvDownloadDescription"),
    sendEmailOnCampActivation:t("sendEmailOnCampActivationDescription"),
    useHttp1Protocol: t("useHttp1ProtocolLabelDescription"),
    dataManagement: t("dataManagementDecriptionLabel"),
    referenceData: t("referenceDataDescriptionLabel"),
    fetchEventsFromMongo:t("fetchEventsFromMongoDescriptionLabel"),
    topicOnly:t("topicOnlyDescriptionLabel"),
        segmentedPush:t("segmentedPushDescriptionLabel"),
    campaignReportDatewiseGraph:t("campaignDatewiseGraphDescriptionLael"),
    dismissPushfromTray:t("dismissPushfromTrayDescription"),
    getAppEnabledForPush:t("getAppEnabledForPushDescriptionLabel"),
    getAppEnabledForGeo:t("getAppEnabledForGeoDescriptionLabel"),
    genericProfile:t("genericProfileDescription"),
    campaignThreshold:t("campaignThresholdDescriptionLabel"),
    staggeredSend:t("staggeredSettingDescriptionLabel"),
    budget:t("budgetSettingDescriptionLabel"),
    rateLimit:t("rateLimitDescriptionLabel"),
    isAutomaticUploadOfCertificates:t("isAutomaticUploadOfCertificatesDecLabel"),
    campaignAuditTrail:t("campaignAuditTrailDescritionLabel"),
    actionButtonCopyShare:t("actionButtonCopyShareDescription"),
    parallelJobs:t("parallelJobsDescriptionLabel"),
    dryRun:t("dryRunDescriptionLabel")






  };

  // Render features for the active tab
  const renderFeatures = (activeTab: string) => {
    const features = featureGroups[activeTab];
    if (!features) return null;
    return features.map((featureKey) => (
      <Form.Item
        key={featureKey}
        name={featureKey}
        initialValue={featureData[featureKey]}
      >
        <CustomCheckbox
          label={
            <span>
              {labels[featureKey] || featureKey}
              {descriptions[featureKey] && (
                <InfoCircleOutlined
                  title={descriptions[featureKey]}
                  className="feature-description-icon"
                />
              )}
            </span>
          }
          checked={
            featureData[featureKey] === undefined
              ? true
              : featureData[featureKey]
          }
          onChange={(e: any) => handleChange(featureKey, e.target.checked)}
        />
      </Form.Item>
    ));
  };
  return (
    <div className="feature-setting-container">
      <div className="mt-4 mb-4">
        <h6 className="feature-setting-container-heading">
          {t("featuresSettingHeadingLabel")}
        </h6>
        <Divider className="feature-setting-custom-divider" />
      </div>

      <div className="mt-3">
        <p>{t("featuresSettingDescriptionLabel")}</p>
        <div className="mt-3 align-items-center feature-setting-form">
          {showNoDataMessage ? (
            <NoData />
          ) : loading ? (
            <Spinner />
          ) : (
            <Form onFinish={handleSubmit}>
              <Tabs
                defaultActiveKey="frontend"
                items={[
                  {
                    key: "frontend",
                    label: t("frontendLabel"),
                    children: (
                      <Tabs
                        defaultActiveKey="dashboard"
                        items={[
                          {
                            key: "dashboard",
                            label: t("dashboardLabel"),
                            children: renderFeatures("dashboard"),
                          },
                          {
                            key: "users",
                            label: t("usersLabel"),

                            children: (
                              <Tabs
                                defaultActiveKey="users"
                                items={[
                                  {
                                    key: "customer360",
                                    label: t("customer360Label"),
                                    children: renderFeatures("customer360"),
                                  },
                                  {
                                    key: "audience",
                                    label: t("audienceLabel"),
                                    children: renderFeatures("audience"),
                                  },
                                  {
                                    key: "traits",
                                    label: t("traitsLabel"),
                                    children: renderFeatures("traits"),
                                  },
                                ]}
                              />
                            ),
                          },
                          {
                            key: "analytics",
                            label: t("analyticsLabel"),

                            children: (
                              <Tabs
                                defaultActiveKey="acquisition"
                                items={[
                                  {
                                    key: "acquisition",
                                    label: t("acquisitionLabel"),
                                    children: renderFeatures("acquisition"),
                                  },
                                  {
                                    key: "funnel",
                                    label: t("funnelLabel"),
                                    children: renderFeatures("funnel"),
                                  },
                                ]}
                              />
                            ),
                          },

                          {
                            key: "general-campaign",
                            label: t("engagementLabel"),
                            children: (
                              <Tabs
                                defaultActiveKey="campaign-analytics"
                                items={[
                                  {
                                    key: "campaign-analytics",
                                    label: t("campaignAnalyticsLabel"),
                                    children:
                                      renderFeatures("campaign-analytics"),
                                  },
                                  {
                                    key: "create",
                                    label: t("createLabel"),
                                    children: renderFeatures("create"),
                                  },

                                  {
                                    key: "launch-campaign",
                                    label: t("launchCampaignLabel"),
                                    children: renderFeatures("launch-campaign"),
                                  },
                                  {
                                    key: "campaign-details",
                                    label: t("campaignDetailsLabel"),
                                    children:
                                      renderFeatures("campaign-details"),
                                  },
                                ]}
                              />
                            ),
                          },

                          {
                            key: "customer-data-platform",
                            label: t("sideNavLabel"),
                            children: renderFeatures("customerDataPlatform"),
                          },
                          {
                            key: "legacy",
                            label: t("legacyLabel"),
                            children: renderFeatures("legacy"),
                          },
                        ]}
                      />
                    ),
                  },
                  {
                    key: "backend",
                    label: t("backendLabel"),
                    children: (
                      <Tabs
                        defaultActiveKey="pipeline"
                        items={[
                          {
                            key: "pipeline",
                            label: t("pipelineLabel"),
                            children: renderFeatures("pipeline"),
                          },
                          {
                            key: "api",
                            label: t("apiLabel"),
                            children: renderFeatures("api"),
                          }
                        
                        ]}
                      />
                    ),
                  },
                ]}
              />

              <Form.Item>
                <PrimaryButton
                  size="middle"
                  type="primary"
                  htmlType="submit"
                  className="feature-setting-btn-save"
                >
                  {t("saveLabel")}
                </PrimaryButton>
              </Form.Item>
            </Form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Features;
